import React, { useEffect, useState } from "react";
import { ScreenHeader } from "react-library";
import { useParams } from "react-router-dom";
import {
  getWeatherData,
  TWeatherDataResponse,
  DailyWeatherData,
} from "../../services/farm.service";
import { t } from "../../services/locale.service";
import dayjs from "dayjs";
import Images from "../../assets/images";
import { getWeatherIcon } from "../orgPlotChart/util";
import "./weather.css";

const Weather: React.FC = () => {
  const { plotId } = useParams();
  const [weatherData, setWeatherData] = useState<TWeatherDataResponse>();

  const headerTitle = t("weather");

  useEffect(() => {
    window.addEventListener("back-icon-press", handleBack);
    return () => {
      window.removeEventListener("back-icon-press", handleBack);
    };
  }, []);

  useEffect(() => {
    if (plotId) {
      getWeatherData(plotId)
        .then((res) => {
          setWeatherData(res);
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, []);

  const handleBack = () => {
    try {
      window.history.back();
    } catch (error) {
      console.log(error);
    }
  };

  const notUndefinedNull = (value: number | string) =>
    value !== undefined && value !== null;

  const getHourlyWeatherIcon = (icon: string, time: string) => {
    icon = icon || "";
    icon = icon.toLowerCase();
    //   fog_light:'Light Fog',
    // fog :' Fog',
    if (icon.indexOf("cloud") > -1 && icon.indexOf("part") > -1) {
      if (
        parseInt(dayjs(time).format("HH")) >= 19 ||
        parseInt(dayjs(time).format("HH")) <= 6
      ) {
        return Images.weatherNightPartlyCloudy;
      }
      return Images.weatherPartlyCloudy;
    }

    if (icon.indexOf("most") > -1 && icon.indexOf("cloud") > -1) {
      if (
        parseInt(dayjs(time).format("HH")) >= 19 ||
        parseInt(dayjs(time).format("HH")) <= 6
      )
        return Images.weatherNightPartlyCloudy;
      return Images.mostlyCloudy;
    }
    if (icon.indexOf("cloud") > -1) {
      return Images.weatherCloudy;
    }

    // clear or sunny
    if (icon.indexOf("clear") > -1 && icon.indexOf("part") > -1) {
      if (
        parseInt(dayjs(time).format("HH")) >= 19 ||
        parseInt(dayjs(time).format("HH")) <= 6
      )
        return Images.weatherNightPartlyCloudy;
      return Images.partlySunny;
    }
    if (icon.indexOf("clear") > -1 && icon.indexOf("most") > -1) {
      if (
        parseInt(dayjs(time).format("HH")) >= 19 ||
        parseInt(dayjs(time).format("HH")) <= 6
      )
        return Images.weatherNightPartlyCloudy;
      return Images.weatherPartlyCloudy;
    }
    if (icon.indexOf("clear") > -1) {
      if (
        parseInt(dayjs(time).format("HH")) >= 19 ||
        parseInt(dayjs(time).format("HH")) <= 6
      )
        return Images.weatherNight;
      return Images.weatherSunny;
    }
    if (icon.indexOf("cold") > -1 && icon.indexOf("rain")) {
      return Images.weatherSnowyRainy;
    }

    // rain
    if (
      icon.indexOf("rain") > -1 ||
      icon.indexOf("shower") > -1 ||
      icon.indexOf("drizzle") > -1
    ) {
      return Images.weatherRainy;
    }
    if (icon.indexOf("hail") > -1) {
      return Images.hail;
    }
    if (icon.indexOf("thunder") > -1) {
      return Images.weatherLightning;
    }

    // ice
    if (icon.indexOf("ice") > -1) {
      return Images.ice;
    }
    if (icon.indexOf("flurries") > -1 || icon.indexOf("snow") > -1) {
      return Images.weatherSnowy;
    }

    // fog
    if (icon.indexOf("fog") > -1) {
      return Images.fogLight;
    }

    //  hazy sunshine
    if (icon.indexOf("smoke") > -1 || icon.indexOf("haz") > -1) {
      return Images.hazySunshine;
    }

    // wind
    if (icon.indexOf("wind") > -1) {
      return Images.wind;
    }
    if (icon.indexOf("hurricane") > -1) {
      return Images.hurricane;
    }
  };

  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const expandDailyCard = (timestamp: string) => {
    setExpandedItem(expandedItem === timestamp ? null : timestamp);
  };

  const WeatherCardBottomGrid = (props: { item: DailyWeatherData }) => {
    const item = props.item;
    return (
      <div className="weather-details-container">
        {item?.rainFall && (
          <div className="weather-details-section rain">
            <p style={{ fontWeight: "normal", margin: 0 }}>{t("rainFall")}</p>
            <p>{item?.rainFall?.value?.toFixed(2)} mm</p>
          </div>
        )}

        {item?.windSpeed && (
          <div className="weather-details-section wind">
            <p style={{ fontWeight: "normal", margin: 0 }}>{t("windSpeed")}</p>
            <p>
              {item?.windSpeed?.avg?.value?.toFixed(0)} km/hr |{" "}
              {item?.windDirection?.avg?.value}
            </p>
          </div>
        )}

        {item?.airHumidity && (
          <div
            className={`weather-details-section ${
              item?.lightIntensity ? "humidity" : ""
            }`}
          >
            <p style={{ fontWeight: "normal", margin: 0 }}>
              {t("airHumidity")}
            </p>
            <p>{item?.airHumidity?.avg?.value?.toFixed(1)} %</p>
          </div>
        )}

        {item?.lightIntensity && (
          <div className="weather-details-section">
            <div>
              <p style={{ fontWeight: "normal", margin: 0 }}>
                {t("lightIntensity")}
              </p>
            </div>
            <div className="weather-details-divider"></div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "80px",
              }}
            >
              <div>
                <p className="small-text" style={{ margin: 0 }}>
                  {t("average")}
                </p>
                <p>{item?.lightIntensity?.avg?.value?.toFixed(0)}</p>
              </div>
              <div>
                <p className="small-text" style={{ margin: 0 }}>
                  {t("Maximum")}
                </p>
                <p>{item?.lightIntensity?.max?.value?.toFixed(0)}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (
    !weatherData?.dailyWeatherForecastData.length &&
    !weatherData?.hourlyWeatherForecastData.length
  ) {
    return (
      <div>
        <ScreenHeader screenName={headerTitle} />
        <p style={{ textAlign: "center" }}>{t("No_Data_Found")}!</p>
      </div>
    );
  }

  return (
    <div>
      <ScreenHeader screenName={headerTitle} />
      {!plotId && <div> {t("No_Plot_Selected")} </div>}
      <p style={{ padding: "0px 14px", fontSize: "12px", fontWeight: "bold" }}>
        {t("hourlyPrediction")}
      </p>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          flexDirection: "row",
          overflowX: "auto",
          whiteSpace: "nowrap",
        }}
      >
        {weatherData?.hourlyWeatherForecastData?.map((item, index) => (
          <div key={item.timestamp + index} className="weather-card">
            <div className="time-weather-temp">
              <p>
                {new Date().getDate() === new Date(item?.timestamp).getDate()
                  ? dayjs(item?.timestamp).format("h A")
                  : dayjs(item?.timestamp).format("DD-MM h A")}
              </p>
              <img
                src={getHourlyWeatherIcon(
                  item.hasPrecipitation.value
                    ? "Rain"
                    : item?.weatherIcon?.value,
                  item.timestamp
                )}
                alt="Weather Icon"
                className="weather-icon"
              />
            </div>

            <p style={{ fontSize: "11px", textAlign: "center", margin: 0 }}>
              {t("airTemp")}
            </p>
            <p className="temperature" style={{ margin: 0 }}>
              {item?.airTemp?.value?.toFixed(0)}°C
            </p>

            <div style={{ margin: "10px 0px" }}>
              <div className="info-row" style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "11px", textAlign: "center" }}>
                  <p
                    style={{ fontSize: "11px", textAlign: "center", margin: 0 }}
                  >
                    {t("rainFall")}
                  </p>
                  <p style={{ margin: 1 }}>
                    {(Number(item?.rainFall?.value?.toFixed(2)) || "0") + "mm"}
                  </p>
                </div>

                <div className="info-column">
                  <div className="wind-speed">
                    <div
                      style={{
                        fontSize: "11px",
                        textAlign: "center",
                        margin: 0,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "11px",
                          textAlign: "center",
                          margin: 0,
                        }}
                      >
                        {t("windSpeed")}
                      </p>
                      <p style={{ margin: 1 }}>
                        {item?.windSpeed?.value?.toFixed(0)} km/hr |{" "}
                        <span style={{ fontSize: "11px" }}>
                          {item?.windDirection?.value}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="info-row">
                <div style={{ fontSize: "11px", textAlign: "center" }}>
                  <p
                    style={{ fontSize: "11px", textAlign: "center", margin: 0 }}
                  >
                    {t("airHumidity")}
                  </p>
                  <p style={{ margin: 1 }}>
                    {item?.airHumidity?.value?.toFixed(1)} %
                  </p>
                </div>

                <div className="info-column">
                  <div className="wind-speed">
                    <div
                      style={{
                        fontSize: "11px",
                        textAlign: "center",
                        margin: 0,
                      }}
                    >
                      <p
                        style={{
                          fontSize: "11px",
                          textAlign: "center",
                          margin: 0,
                        }}
                      >
                        {t("lightIntensity")}
                      </p>
                      <p style={{ margin: 1 }}>
                        {item?.lightIntensity
                          ? item?.lightIntensity?.value?.toFixed(0)
                          : " "}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      <p style={{ padding: "0px 14px", fontSize: "12px", fontWeight: "bold" }}>
        {t("dailyPrediction")}
      </p>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          minHeight: "100vh",
          padding: "0 10px",
        }}
      >
        {weatherData?.dailyWeatherForecastData?.map((item, index) => {
          const hasPrecipitation =
            item?.hasPrecipitation?.value && item?.rainFall?.value > 0;

          return (
            <div
              key={index}
              className={`daily-card ${
                hasPrecipitation ? "daily-card-precipitation" : ""
              }`}
              onClick={() => expandDailyCard(item?.timestamp)}
              style={{ marginBottom: "8px" }}
            >
              <div className="daily-card-header">
                <div className="daily-card-header-left">
                  <p>
                    {item?.timestamp &&
                      new Date(item.timestamp).toLocaleDateString()}
                  </p>
                </div>

                <div
                  className="daily-card-header-right"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    fontSize: "12px",
                    width: "20%",
                    justifyContent: "space-between",
                  }}
                >
                  <p className="rain-info">
                    {hasPrecipitation
                      ? "Rain"
                      : item?.weatherIcon?.displayValue ??
                        item?.iconPhrase?.value}
                  </p>
                  <img
                    src={getWeatherIcon(
                      hasPrecipitation ? "Rain" : item?.weatherIcon?.value
                    )}
                    alt="Weather Icon"
                    className="daily-card-icon"
                  />
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-evenly",
                  textAlign: "center",
                  fontSize: "11px",
                }}
              >
                {notUndefinedNull(item?.airTemp?.min?.value) && (
                  <div>
                    <p style={{ margin: 0 }}>{t("Minimum")}</p>
                    <p
                      style={{
                        fontWeight: "bolder",
                        fontSize: "14px",
                        margin: 2,
                      }}
                    >
                      {item?.airTemp?.min?.value?.toFixed(0)}°C
                    </p>
                  </div>
                )}

                {notUndefinedNull(item?.airTemp?.avg?.value) && (
                  <div>
                    <p style={{ margin: 0 }}>{t("airTemp")}</p>
                    <p
                      style={{
                        fontWeight: "bolder",
                        fontSize: "14px",
                        margin: 2,
                      }}
                    >
                      {item?.airTemp?.avg?.value?.toFixed(0)}°C
                    </p>
                  </div>
                )}
                {notUndefinedNull(item?.airTemp?.max?.value) && (
                  <div>
                    <p style={{ margin: 0 }}>{t("Maximum")}</p>
                    <p
                      style={{
                        fontWeight: "bolder",
                        fontSize: "14px",
                        margin: 2,
                      }}
                    >
                      {item?.airTemp?.max?.value?.toFixed(0)}°C
                    </p>
                  </div>
                )}
              </div>

              <div className="daily-card-bottom">
                <WeatherCardBottomGrid item={item} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Weather;
